import { Component, inject, input, output, signal, computed, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { CatalogService } from '@services/data/catalog.service';
import { AuthService } from '@services/core/auth.service';
import { AppSettings } from '@services/settings/app-settings.service';
import { RequestService } from '../../services/requests.service';
import { BpmService } from '../../services/bpm.service';
import { ViewerComponent, DocViewerItem } from '../../../management-document/components/viewer/viewer.component';
import { StepApprovalModalComponent, StepApprovalPayload } from '../step-approval-modal/step-approval-modal.component';
import { FlowStepsComponent } from 'app/core/components/flow-steps/flow-steps.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { SafeUrlPipe } from 'app/core/pipes/safe-url.pipe';
import { Global } from 'app/core/service/core/Global';
import { DocumentService } from 'app/feature/management-document/services/document.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-request-payment',
  standalone: true,
  imports: [
    CommonModule,
    ViewerComponent,
    SafeUrlPipe,
    StepApprovalModalComponent,
    FlowStepsComponent,
    MobileFooterComponent
  ],
  templateUrl: './request-payment.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./request-payment.component.scss']
})
export class RequestPaymentComponent {
  request = input.required<any>();
  onApproved = output<void>();
  onRejected = output<void>();

  @ViewChild(StepApprovalModalComponent) approvalModal!: StepApprovalModalComponent;

  private catalogService = inject(CatalogService);
  private authService = inject(AuthService);
  private requestService = inject(RequestService);
  private bpmService = inject(BpmService);
  private documentService = inject(DocumentService);
  readonly appSettings = inject(AppSettings);

  isViewerOpen = signal(false);
  viewerItems = signal<DocViewerItem[]>([]);
  activeDocId = signal<string | number>('');
  isProcessingApproval = signal(false);

  private role = computed(() => {
    const roleObj = this.authService.sharingRole() as any;
    return (roleObj?.slug ?? roleObj?.name ?? 'VISUALIZADOR').toUpperCase();
  });

  private userMail = computed(() => {
    const identity = this.authService.sharingIdentity() as any;
    const data = this.authService.userData() as any;
    return String(identity?.mail ?? data?.mail ?? '').trim().toLowerCase() || null;
  });

  isAdjustment = computed(() => this.request()?.processSlug === 'ADJUST_ENTITY');
  canApprove = computed(() => this.bpmService.canApprove(this.request(), this.role()));
  canObserve = computed(() => this.bpmService.canObserve(this.request(), this.role()));
  canResubmit = computed(() =>
    this.bpmService.canResubmitObservation(this.request(), this.role(), this.userMail())
  );
  isObserved = computed(() => this.bpmService.isObserved(this.request()));
  observationReason = computed(() => this.bpmService.observationReason(this.request()));
  nextStatusSlug = computed(() => this.bpmService.nextStatusSlug(this.request()));
  isClosed = computed(() => this.bpmService.isClosed(this.request()));
  statusLabel = computed(() => this.catalogService.getStatusName(this.request()?.statusSlug));
  stepRequiresDoc = computed(() => this.bpmService.stepRequiresDocument(this.request()) || this.bpmService.isPaymentStep(this.request()));

  detailsText = computed(() => String(this.request()?.payment?.details ?? this.request()?.details ?? '').trim());
  isCargo = computed(() => this.detailsText().includes('[AJUSTE CARGO]'));
  titleLabel = computed(() => this.isAdjustment() ? 'Detalle del Ajuste Contable' : 'Detalle del Pago');
  subtitleLabel = computed(() => this.isAdjustment() ? 'Revisa la glosa y monto del ajuste antes de dictaminar' : 'Revisa el comprobante y el monto antes de aprobar');
  movementTypeBadge = computed(() => {
    if (!this.isAdjustment()) return 'Pago Regular';
    return this.isCargo() ? 'Ajuste Cargo (Aumenta Deuda)' : 'Ajuste Abono (Reduce Deuda)';
  });

  showBpmActions = computed(() =>
    this.canResubmit() || this.canApprove() || this.canObserve()
  );

  showMobileFooter = computed(() =>
    this.appSettings.isMobile() && this.showBpmActions() && !this.approvalModalOpen()
  );

  /** El modal fullscreen móvil está abierto (oculta footer del detalle). */
  approvalModalOpen = signal(false);

  approveBtnLabel = computed(() => {
    if (this.isAdjustment()) return 'Aprobar Ajuste';
    if (!this.documentUuid() && this.stepRequiresDoc()) {
      return this.appSettings.isMobile() ? 'Adjuntar y aprobar' : 'Adjuntar Comprobante y Aprobar';
    }
    return 'Aprobar Pago';
  });

  documentUuid = computed(() => {
    const req = this.request();
    if (!req) return null;
    const fromPayment = req.payment?.document?.uuid;
    if (fromPayment) return fromPayment;
    const docs = req.documents ?? [];
    const first = docs[0]?.document ?? docs[0];
    if (first?.uuid) return first.uuid;
    const fromDecision =
      req.paymentDecision?.documentUuid
      || req.commissionDecision?.documentUuid
      || null;
    if (fromDecision) return fromDecision;
    const history = [...(req.flowHistory ?? req.flowSteps ?? [])]
      .slice()
      .reverse();
    const stepWithDoc = history.find((s: any) => s?.documentUuid);
    return stepWithDoc?.documentUuid ?? null;
  });

  docUrl = computed(() => {
    const uuid = this.documentUuid();
    return uuid ? `${Global.url}V1/documents/view/${uuid}` : '';
  });

  async openViewer(): Promise<void> {
    const uuid = this.documentUuid();
    if (!uuid) return;
    const title = this.isAdjustment() ? 'Respaldo del Ajuste' : 'Comprobante de Pago';
    let mimeType: string | null = this.request()?.payment?.document?.mimeType ?? null;
    let fileName: string | null = this.request()?.payment?.document?.fileName ?? null;
    if (!mimeType) {
      try {
        const doc = await firstValueFrom(this.documentService.getByUuid(uuid));
        mimeType = doc?.mimeType ?? null;
        fileName = doc?.fileName ?? fileName;
      } catch {
        /* sin metadatos: el visor intenta por URL */
      }
    }
    const kind = String(mimeType || '').includes('pdf') || /\.pdf$/i.test(fileName || '')
      ? 'pdf' as const
      : undefined;
    this.viewerItems.set([{
      id: uuid,
      title,
      imageUrl: this.docUrl(),
      mimeType,
      fileName,
      kind
    }]);
    this.activeDocId.set(uuid);
    this.isViewerOpen.set(true);
  }

  getEntityName(code: number | string): string {
    if (!code) return 'N/A';
    const entity = this.catalogService.entities().find(e => Number(e.code) === Number(code));
    return entity ? `${entity.code} - ${entity.name}` : `Entidad ${code}`;
  }

  openApproval(): void {
    this.approvalModalOpen.set(true);
    this.approvalModal?.open();
  }

  onApprovalCancelled(): void {
    this.approvalModalOpen.set(false);
  }

  approve(): void {
    if (this.stepRequiresDoc() && !this.documentUuid()) {
      this.openApproval();
      return;
    }
    this.executeDirectApproval();
  }

  private executeDirectApproval(docUuid?: string, gloss?: string): void {
    this.requestService.advanceFlowStep(this.request().uuid, {
      toStatusSlug: this.nextStatusSlug(),
      gloss: gloss ?? '',
      documentUuid: docUuid ?? this.documentUuid()
    }).subscribe(() => this.onApproved.emit());
  }

  async onApprovalConfirmed(payload: StepApprovalPayload): Promise<void> {
    if (this.isObserved()) {
      await this.executeResubmitWithPayload(payload);
      return;
    }
    this.isProcessingApproval.set(true);
    try {
      let docUuid = payload.documentUuid;
      if (payload.file) {
        const res = await firstValueFrom(this.requestService.uploadPaymentDocument(this.request().uuid, payload.file));
        docUuid = res?.document?.uuid ?? res?.uuid ?? docUuid;
      }
      await firstValueFrom(this.requestService.advanceFlowStep(this.request().uuid, {
        toStatusSlug: this.nextStatusSlug(),
        gloss: payload.gloss,
        documentUuid: docUuid,
        documentTypeName: payload.documentTypeName,
        requiresDocument: payload.requiresDocument,
        paymentDate: payload.payDetails?.payDate ?? null
      }));
      this.approvalModal?.forceClose();
      this.approvalModalOpen.set(false);
      this.onApproved.emit();
    } catch (err: any) {
      const msg = err?.error?.error || err?.error?.message || err?.message;
      await Swal.fire({ icon: 'error', title: 'Error al procesar pago', text: msg || 'Intente nuevamente.' });
    } finally {
      this.isProcessingApproval.set(false);
    }
  }

  async observe(): Promise<void> {
    const { value: reason } = await Swal.fire({
      title: 'Observar solicitud',
      input: 'textarea',
      inputLabel: 'Motivo de la observación (obligatorio)',
      inputPlaceholder: 'Ej: El comprobante está borroso, vuelva a subir una foto legible...',
      inputValidator: (v) => !v?.trim() ? 'El motivo es obligatorio' : null,
      showCancelButton: true,
      confirmButtonText: 'Observar',
      confirmButtonColor: '#f0ad4e'
    });
    if (!reason?.trim()) return;
    this.isProcessingApproval.set(true);
    try {
      await firstValueFrom(this.requestService.advanceFlowStep(this.request().uuid, {
        toStatusSlug: this.bpmService.observeTargetSlug(this.request()),
        comments: reason.trim()
      }));
      this.onApproved.emit();
    } catch (err: any) {
      const msg = err?.error?.error || err?.error?.message || err?.message;
      await Swal.fire({ icon: 'error', title: 'No se pudo observar', text: msg || 'Intente nuevamente.' });
    } finally {
      this.isProcessingApproval.set(false);
    }
  }

  resubmitObservation(): void {
    const target = this.bpmService.resubmitTargetSlug(this.request());
    if (!target) {
      Swal.fire('Atención', 'No hay destino configurado para subsanar.', 'info');
      return;
    }
    this.openApproval();
  }

  private async executeResubmitWithPayload(payload: StepApprovalPayload): Promise<void> {
    const target = this.bpmService.resubmitTargetSlug(this.request());
    if (!target) {
      await Swal.fire('Atención', 'No hay destino configurado para subsanar.', 'info');
      return;
    }
    if (!payload.file) {
      await Swal.fire('Atención', 'Debe adjuntar el nuevo comprobante para reenviar.', 'info');
      return;
    }
    this.isProcessingApproval.set(true);
    try {
      const res = await firstValueFrom(this.requestService.uploadPaymentDocument(this.request().uuid, payload.file));
      const docUuid = res?.document?.uuid ?? res?.uuid ?? null;
      await firstValueFrom(this.requestService.advanceFlowStep(this.request().uuid, {
        toStatusSlug: target,
        gloss: payload.gloss,
        documentUuid: docUuid,
        documentTypeName: payload.documentTypeName,
        requiresDocument: true,
        paymentDate: payload.payDetails?.payDate ?? null
      }));
      this.approvalModal?.forceClose();
      this.approvalModalOpen.set(false);
      this.onApproved.emit();
    } catch (err: any) {
      const msg = err?.error?.error || err?.error?.message || err?.message;
      await Swal.fire({ icon: 'error', title: 'No se pudo reenviar', text: msg || 'Intente nuevamente.' });
    } finally {
      this.isProcessingApproval.set(false);
    }
  }

  async reject(): Promise<void> {
    const title = this.isAdjustment() ? 'Rechazar Ajuste' : 'Rechazar Pago';
    const { value: reason } = await Swal.fire({
      title, input: 'textarea',
      inputLabel: 'Motivo del Rechazo (Obligatorio)',
      inputPlaceholder: 'Escriba por qué se rechaza...',
      inputValidator: (v) => !v ? 'El motivo es obligatorio' : null,
      showCancelButton: true, confirmButtonText: 'Confirmar Rechazo', confirmButtonColor: '#d33'
    });
    if (!reason) return;
    this.requestService.advanceFlowStep(this.request().uuid, {
      toStatusSlug: 'REJECTED',
      reason
    }).subscribe(() => this.onRejected.emit());
  }
}
